import { onlineManager } from '@tanstack/react-query';
import { useSyncExternalStore } from 'react';
import { nativeOnline, onNative } from '@/shared/lib/nativeApp';
import { readStorage, writeStorage } from '@/shared/lib/storage';

const SEEN_KEY = 'chat.offline.seen';

/** Когда связь была в последний раз: знак «Офлайн · обновлено 14:20». null — не знаем (первый запуск без сети). */
export function lastOnlineAt(): number | null {
  if (online) return Date.now();
  const n = Number(readStorage(SEEN_KEY));
  return Number.isFinite(n) && n > 0 ? n : null;
}

/**
 * Есть ли связь с сервером. Источники: приложение для Mac и iPhone (NWPathMonitor), события браузера
 * и сам запрос — если он не дошёл до сервера, связи нет, даже когда система уверена в обратном.
 */
let online = nativeOnline() ?? (typeof navigator === 'undefined' ? true : navigator.onLine);
const listeners = new Set<() => void>();

export function isOnline(): boolean {
  return online;
}

export function setOnline(value: boolean): void {
  if (value === online) return;
  // Связь пропала: запоминаем, до какого момента всё на устройстве свежее.
  if (!value) writeStorage(SEEN_KEY, String(Date.now()));
  online = value;
  // TanStack Query сам ставит запросы на паузу без сети и перечитывает их, когда она вернулась.
  onlineManager.setOnline(value);
  listeners.forEach((l) => {
    l();
  });
}

export function subscribeOnline(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function useOnline(): boolean {
  return useSyncExternalStore(subscribeOnline, isOnline, isOnline);
}

const PROBE_MS = 4000;

/** Пока связи нет — раз в несколько секунд спрашиваем сервер: ответил, значит вернулась. */
async function probe(): Promise<boolean> {
  try {
    const response = await fetch('/api/auth/csrf', { credentials: 'same-origin', cache: 'no-store' });
    // Страница входа в Wi-Fi отвечает 200 и HTML: онлайн только если ответил наш сервер.
    const body: unknown = await response.json();
    return response.ok && typeof body === 'object' && body !== null && typeof Reflect.get(body, 'token') === 'string';
  } catch {
    return false;
  }
}

/** Запускается один раз при старте страницы; возвращает функцию остановки. */
export function watchNetwork(): () => void {
  setOnline(nativeOnline() ?? navigator.onLine);
  const up = () => {
    setOnline(true);
  };
  const down = () => {
    setOnline(false);
  };
  window.addEventListener('online', up);
  window.addEventListener('offline', down);
  const stopNative = onNative((e) => {
    if (e.type === 'network') setOnline(e.online);
  });
  const timer = window.setInterval(() => {
    if (online) return;
    void probe().then((ok) => {
      if (ok) setOnline(true);
    });
  }, PROBE_MS);
  return () => {
    window.removeEventListener('online', up);
    window.removeEventListener('offline', down);
    stopNative();
    window.clearInterval(timer);
  };
}
